"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.roundHalfEven = roundHalfEven;
exports.toFixedNumber = toFixedNumber;
exports.getPeriodicRate = getPeriodicRate;
exports.getDaysInTerm = getDaysInTerm;
exports.getDailyRate = getDailyRate;
exports.getPaymentRate = getPaymentRate;
exports.getAnnualRate = getAnnualRate;
exports.getPaymentAmount = getPaymentAmount;
exports.getEffectivePaymentCount = getEffectivePaymentCount;
exports.findNewPaymentFrequency = findNewPaymentFrequency;
exports.calculateEffectivePaymentCount = calculateEffectivePaymentCount;
exports.findTotalInterest = findTotalInterest;
exports.findFinalPaymentAmount = findFinalPaymentAmount;
exports.getPeriodicLabel = getPeriodicLabel;
exports.prettyFrequency = prettyFrequency;
const get = require('lodash/get');
const invert = require('lodash/invert');
/**
 * Map of frequency name to value
 */
const FREQ_MAP = {
    'daily': 1,
    'weekly': 7,
    'semiMonthly': 14,
    'monthly': 30,
};
function roundHalfEven(num) {
    const decCheck = num - Math.floor(num);
    if (decCheck === 0.5) {
        return (2 * Math.round(num / 2));
    }
    else {
        return Math.round(num);
    }
}
function toFixedNumber(num, digits, base = 10) {
    const pow = Math.pow(base, digits);
    return Math.round(num * pow) / pow;
}
function getPeriodicRate(params) {
    // ensure valid argument types
    if (typeof params.annualRate === 'number' && typeof params.periodsPerYear === 'number') {
        return toFixedNumber(params.annualRate / params.periodsPerYear, 10);
    }
}
function getDaysInTerm(params) {
    // ensure valid argument types
    if (typeof params.termMonths === 'number') {
        return toFixedNumber(365 / 12 * params.termMonths, 2);
    }
}
function getDailyRate(params) {
    // ensure valid argument types
    if (typeof params.annualRate === 'number') {
        return toFixedNumber(params.annualRate / 365, 10);
    }
}
function getPaymentRate(params) {
    // ensure valid argument types
    if (typeof params.dailyRate === 'number' && typeof params.daysInPeriod === 'number') {
        return toFixedNumber(params.dailyRate * params.daysInPeriod, 10);
    }
}
function getAnnualRate(params) {
    // ensure valid argument types
    if (typeof params.periodicRate === 'number' && typeof params.periodsPerYear === 'number') {
        return toFixedNumber(params.periodicRate * params.periodsPerYear, 10);
    }
}
function getPaymentAmount(params) {
    // ensure valid argument types
    if (typeof params.drawAmount === 'number' && typeof params.periodicRate === 'number' && typeof params.paymentCount === 'number') {
        return toFixedNumber(Math.ceil(params.drawAmount * (params.periodicRate / 100) / (1 - Math.pow(1 + (params.periodicRate / 100), -params.paymentCount)) * 100) / 100, 2);
    }
}
function getEffectivePaymentCount(params) {
    // ensure valid argument types
    if (typeof params.termMonths === 'number' && typeof params.paymentCount === 'number' && typeof params.payoffMonth === 'number') {
        return toFixedNumber(Math.round(params.payoffMonth * (params.paymentCount / params.termMonths)), 2);
    }
}
/**
 * Supporting function for findNewPaymentFrequency.
 */
function getNearestPaymentFrequency(paymentFrequency, possibleFrequencies = []) {
    const frequency = possibleFrequencies.find(freq => freq === paymentFrequency);
    if (frequency) {
        return frequency;
    }
    const frequencyValues = possibleFrequencies.map(freq => FREQ_MAP[freq] || 0);
    const goalValue = FREQ_MAP[paymentFrequency] || 0;
    const closestValue = frequencyValues.reduce(function (prev, curr) {
        return (Math.abs(curr - goalValue) < Math.abs(prev - goalValue) ? curr : prev);
    }, 0);
    const reverseFreqMap = invert(FREQ_MAP);
    if (reverseFreqMap[closestValue]) {
        return reverseFreqMap[closestValue];
    }
    return possibleFrequencies[0];
}
/**
 * Determine the closest available payment frequency for the provided term value
 */
function findNewPaymentFrequency(termValue, options, selectedPaymentFrequency) {
    const possiblePayFrequency = options
        .filter((opt) => opt.term === termValue)
        .map((opt) => opt.paymentFrequency);
    return getNearestPaymentFrequency(selectedPaymentFrequency, possiblePayFrequency);
}
/**
 * Determine the "effective" payment count given the selected early payoff months
 */
function calculateEffectivePaymentCount(selectedPayoffMonths, numberOfPayments, selectedTerm) {
    return Math.round(selectedPayoffMonths * (numberOfPayments / selectedTerm));
}
/**
 *
 * Find the totalInterest from the payment schedule
 */
function findTotalInterest(paymentSchedule, selectedPayoffMonths, numberOfPayments, selectedTerm) {
    const effectivePaymentCount = calculateEffectivePaymentCount(selectedPayoffMonths, numberOfPayments, selectedTerm);
    // slice the payments schedule rows down to the effective payment count
    return paymentSchedule.slice(0, effectivePaymentCount).reduce((accumulator, scheduleRow) => {
        return toFixedNumber(accumulator + scheduleRow.interestAmount, 2);
    }, 0);
}
function findFinalPaymentAmount(paymentSchedule) {
    return get(paymentSchedule, `[${paymentSchedule.length - 1}].paymentAmount`);
}
/**
 * Determine the correct label to display for the "Periodic Rate"
 */
function getPeriodicLabel(periodsPerYear) {
    switch (periodsPerYear) {
        case 365:
            return 'Daily';
        case 52:
            return 'Weekly';
        case 12:
            return 'Monthly';
        default:
            // Lender sent an invalid value
            return 'Periodic';
    }
}
/**
 * Standardize the payment frequency values for display
 */
function prettyFrequency(frequencyVal) {
    if (frequencyVal.toLowerCase().startsWith('semim')) {
        return 'Semi-Monthly';
    }
    // Capitalize only first letter
    return `${frequencyVal.charAt(0).toUpperCase()}${frequencyVal.slice(1).toLowerCase()}`;
}
